import React, { useState } from 'react';
import { ArrowRight, Linkedin, ChevronDown, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Container from '../common/Container';
import SectionHeading from '../common/SectionHeading';
import Card from '../common/Card';
import { teamMembers } from '../../data/team';

const Team = () => {
  const [expandedMember, setExpandedMember] = useState(null);
  const toggleExpanded = (id) => {
    setExpandedMember(expandedMember === id ? null : id);
  };

  return (
    <section
      id="team"
      className="py-24 bg-gradient-to-b from-white to-slate-50"
      aria-labelledby="team-heading"
    >
      <Container>
        <SectionHeading
          eyebrow="Our Team"
          title={
            <>
              Meet Our{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700 animate-gradient">
                Experts
              </span>
            </>
          }
          description="Deep industry knowledge meets innovative thinking"
          centered
          id="team-heading"
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {teamMembers.sort((a, b) => a.id - b.id).map((member) => (
            <Card
              key={member.id}
              variant="team"
              className="relative bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl group"
              padding="none"
            >
              {/* IMAGE + OVERLAYS */}
              <div className="relative overflow-hidden aspect-square bg-slate-100">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  width={400}
                  height={400}
                  style={{
                    objectPosition: 'center center'
                  }}
                />

                {/* Desktop hover: summary & social */}
                <div
                  className="
                    absolute inset-0 
                    bg-gradient-to-t from-blue-900/95 via-blue-800/90 to-blue-900/80 
                    opacity-0 md:group-hover:opacity-100 invisible md:group-hover:visible 
                    transition-all duration-500 transform translate-y-4 md:group-hover:translate-y-0 
                    flex flex-col justify-center p-6 backdrop-blur-sm
                  "
                >
                  <div className="space-y-4 text-white text-center">
                    <div>
                      <h3 className="text-xl font-bold">{member.name}</h3>
                      <p className="text-cyan-300 text-sm font-medium">{member.title}</p>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">{member.summary}</p>
                  </div>
                </div>

                {/* Mobile strip with summary + toggle */}
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-800/60 to-transparent flex flex-col justify-end p-6 md:hidden">
                  <p className="text-white text-sm mb-4 leading-relaxed">{member.summary}</p>
                  <div className="flex justify-end items-center">
                    <button
                      onClick={() => toggleExpanded(member.id)}
                      className="inline-flex items-center px-3 py-1 rounded-lg bg-white/20 text-white text-xs font-medium hover:bg-white/30 transition-colors duration-300"
                      aria-expanded={expandedMember === member.id}
                    >
                      {expandedMember === member.id ? 'Less' : 'More'}
                      {expandedMember === member.id ? (
                        <X className="ml-1 w-3 h-3" aria-hidden="true" />
                      ) : (
                        <ChevronDown className="ml-1 w-3 h-3" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Mobile expanded overlay */}
                {expandedMember === member.id && (
                  <div className="absolute inset-0 bg-blue-900/95 z-10 p-6 flex flex-col justify-center md:hidden backdrop-blur-sm">
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">{member.name}</h3>
                        <p className="text-cyan-300 text-sm font-medium">{member.title}</p>
                      </div>
                      <button
                        onClick={() => toggleExpanded(null)}
                        className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                        aria-label="Close details"
                      >
                        <X className="w-4 h-4 text-white" aria-hidden="true" />
                      </button>
                    </div>
                    
                    <div className="flex-1 flex items-center">
                      <p className="text-white/90 text-sm leading-relaxed text-center">{member.summary}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* FOOTER: desktop name, title, social */}
              <div className="p-6 flex flex-col flex-grow">
                <div className="mb-4">
                  <h3 className="text-xl font-bold mb-1 text-slate-900">{member.name}</h3>
                  <p className="text-cyan-700 text-sm font-medium leading-relaxed">{member.title}</p>
                </div>
                
                <div className="mt-auto pt-4 border-t border-slate-100 flex justify-start items-center">
                  <div className="flex space-x-3">
                    {member.social.linkedin && (
                      <a
                        href={member.social.linkedin}
                        className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center hover:bg-cyan-200 transition-colors duration-300"
                        aria-label={`${member.name} LinkedIn`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Linkedin className="w-4 h-4 text-cyan-700" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/careers"
            className="inline-flex items-center px-8 py-4 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
          >
            Join Our Team
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </Container>
    </section>
  );
};

export default Team;